import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Leaf, Star, Sparkles, Award, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BREMEN_IMAGE = "https://customer-assets.emergentagent.com/job_dining-guide-3/artifacts/e70golnp_image.png";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dining-guide-3/artifacts/egfzykn3_image.png";

const AboutSection = () => {
  const { t, language } = useLanguage();

  const trustBadges = [
    { icon: Shield, label: t('about.halal'), color: 'emerald', bgColor: 'bg-emerald-500/20', textColor: 'text-emerald-400' },
    { icon: Leaf, label: t('about.fresh'), color: 'green', bgColor: 'bg-green-500/20', textColor: 'text-green-400' },
    { icon: Star, label: `500+ ${t('about.customers')}`, color: 'amber', bgColor: 'bg-amber-500/20', textColor: 'text-amber-400' },
  ];

  return (
    <section 
      id="about" 
      className="relative overflow-hidden"
      data-testid="about-section"
    >
      {/* Premium Section Header with Background Image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BREMEN_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/60 via-hanse-dark/70 to-hanse-dark/95" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          {/* Sparkle Badge - Made highly visible */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 mb-4 bg-gradient-to-r from-amber-500/30 to-amber-400/20 backdrop-blur-sm px-5 py-2.5 rounded-full border-2 border-amber-400/60 shadow-lg shadow-amber-500/20"
          >
            <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
            <span className="text-amber-300 font-bold text-sm md:text-base uppercase tracking-wider">
              {language === 'de' ? 'Seit 2020 in Bremen' : 'Since 2020 in Bremen'}
            </span>
            <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
          >
            {t('about.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-base md:text-lg max-w-2xl"
          >
            {t('about.subtitle')}
          </motion.p>
        </div>
      </div>

      {/* Premium Content */}
      <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left: Story Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10"
            >
              {/* Logo & Brand */}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-hanse-red to-amber-500 rounded-full blur-lg opacity-30"></div>
                  <img
                    src={LOGO_URL}
                    alt="Hanse Pizza Logo"
                    className="relative w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-amber-400/50"
                  />
                </div>
                <div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold">
                    <span className="text-hanse-red">HANSE</span>{' '}
                    <span className="text-white">PIZZA</span>
                  </h3>
                  <p className="text-amber-400 font-accent text-sm">Bremen, Germany</p>
                </div>
              </div>

              {/* Story Text */}
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
                {t('about.story')}
              </p>

              {/* Trust Badges */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {trustBadges.map((badge, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`${badge.bgColor} backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/10 flex items-center gap-3`}
                  >
                    <badge.icon className={`w-5 h-5 md:w-6 md:h-6 ${badge.textColor} flex-shrink-0`} />
                    <span className="text-white/90 font-medium text-sm">{badge.label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: Stats & Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {/* Main Stats Card */}
              <div className="bg-gradient-to-br from-hanse-red/20 to-hanse-red/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-hanse-red/20">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 md:w-12 md:h-12 bg-hanse-red/20 rounded-full flex items-center justify-center">
                    <Award className="w-5 h-5 md:w-6 md:h-6 text-hanse-red" />
                  </span>
                  <h4 className="font-heading text-xl md:text-2xl font-bold text-white">
                    {language === 'de' ? '100% Halal Zertifiziert' : '100% Halal Certified'}
                  </h4>
                </div>
                <p className="text-white/60 text-sm md:text-base">
                  {language === 'de' 
                    ? 'Alle unsere Fleischprodukte sind halal-zertifiziert. Qualität und Vertrauen stehen bei uns an erster Stelle.'
                    : 'All our meat products are halal-certified. Quality and trust are our top priorities.'
                  }
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-white/10 text-center"
                >
                  <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-1">150+</div>
                  <p className="text-white/50 text-xs md:text-sm">
                    {language === 'de' ? 'Menü Artikel' : 'Menu Items'}
                  </p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-white/10 text-center"
                >
                  <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-1">5+</div>
                  <p className="text-white/50 text-xs md:text-sm">
                    {language === 'de' ? 'Jahre Erfahrung' : 'Years Experience'}
                  </p>
                </motion.div>
              </div>

              {/* Location Badge */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-4 md:p-5 border border-white/10 flex items-center gap-4"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 md:w-7 md:h-7 text-amber-400" />
                </div>
                <div>
                  <h5 className="font-heading font-bold text-white text-base md:text-lg">Bremen Neustadt</h5>
                  <p className="text-white/50 text-sm">Industriestraße 12-12A</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
